import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../../apiClient';
import { listOfferings } from '../../../api/offeringsApi';
import QuoteDetail from '../../../pages/Quotes/QuoteDetail';
import './new-quote-drawer.css';

/*
 * NewQuoteDrawer — the single way a quote is started from the customer card.
 *
 * Replaces the old stripped QuoteComposer. A quote opens in a right-side drawer
 * on the real builder (QuoteDetail — the same one /quotes/:id uses, with
 * Preview / Edit / Save / Send), prefilled with this customer.
 *
 * TWO WAYS IN (a bespoke event quote must never be blocked by missing services):
 *   - pick one of the tenant's services   -> draft starts from that offering
 *   - "Bespoke quote (no service)"         -> empty draft, free-form
 * Both POST /quotes/ for the customer (validated on t7: 201, draft with empty
 * items/options) and then open QuoteDetail on the new quote id.
 *
 * Per-quote price override (free-form pricing on a bespoke quote) is RT's coming
 * API addition; QuoteDetail picks it up when it lands — nothing here waits on it.
 */
const money = (v) => {
  if (v === null || v === undefined || v === '') return 'Price on request';
  const n = Number(v);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : 'Price on request';
};

export default function NewQuoteDrawer({ customer, onClose }) {
  // The card record is mapCustomer()'s shape: full_name (which can be the
  // literal fallback "Customer"), phones[]/emails[] arrays, and raw (the full
  // customer with primary_phone/primary_email/customer_id). Read all of them.
  const raw = customer?.raw || {};
  const firstOf = (arr) => {
    const v = Array.isArray(arr) ? arr.find(Boolean) : null;
    return typeof v === 'string' ? v : v?.email || v?.phone || v?.number || v?.value || '';
  };
  const rawName = [raw.first_name, raw.last_name].filter(Boolean).join(' ').trim();
  const customerName =
    (customer?.full_name && customer.full_name !== 'Customer' ? customer.full_name : '') ||
    rawName || raw.full_name || raw.name || raw.company_name || '';
  const customerEmail = raw.primary_email || raw.email || firstOf(customer?.emails) || '';
  const customerPhone = raw.primary_phone || raw.phone || raw.phone_number || firstOf(customer?.phones) || '';
  const customerAddress = raw.address || raw.address_line1 || customer?.address || '';
  // Exact link (POST honours customer_id = pk or UUID once feat/quote-price-override
  // is live). raw.customer_id is the UUID; raw.id is the integer pk; record.id is
  // the customer UUID for a mapped customer. Send the UUID.
  const customerId = raw.customer_id || (typeof customer?.id === 'string' && customer.id.includes('-') ? customer.id : null) || customer?.customer_id || null;
  // Only start a quote when there's a REAL customer to attach — a bare "Customer"
  // fallback with no contact would mint a blank record, so require contact or id.
  const hasCustomer = Boolean(customerEmail || customerPhone || customerId);

  const [offerings, setOfferings] = useState(null); // null = loading
  const [draftId, setDraftId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await listOfferings({ active: true });
        const d = res?.data ?? res;
        const rows = Array.isArray(d) ? d : d?.results || [];
        if (live) setOfferings(rows.filter((o) => o?.active !== false));
      } catch {
        if (live) setOfferings([]); // offerings are optional — bespoke still works
      }
    })();
    return () => { live = false; };
  }, []);

  const createDraft = useCallback(
    async (offeringId) => {
      if (!hasCustomer) {
        setError('This record has no customer details, so a quote can’t be attached to it.');
        return;
      }
      setCreating(true);
      setError('');
      try {
        const payload = {
          status: 'draft',
          customer_name: customerName,
          customer_email: customerEmail,
          customer_phone: customerPhone,
        };
        if (customerId) payload.customer_id = customerId;
        if (customerAddress) payload.address = customerAddress;
        if (offeringId) payload.offering_id = offeringId;
        const res = await api.post('/quotes/', payload);
        const id = res?.data?.id;
        if (!id) throw new Error('The server did not return a quote id.');
        setDraftId(id);
      } catch (err) {
        // Verbatim: string detail/error as-is, field errors spelled out.
        const d = err?.response?.data;
        let msg;
        if (typeof d === 'string' && d.trim()) msg = d.trim();
        else if (d && typeof d === 'object') {
          msg =
            (typeof d.detail === 'string' && d.detail) ||
            (typeof d.error === 'string' && d.error) ||
            Object.entries(d)
              .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(' ') : String(v)}`)
              .join('  •  ');
        }
        setError(msg || err?.message || 'Could not start the quote.');
      } finally {
        setCreating(false);
      }
    },
    [hasCustomer, customerName, customerEmail, customerPhone, customerId, customerAddress]
  );

  const services = useMemo(() => offerings || [], [offerings]);

  return (
    <div className="nqd-scrim" onClick={onClose}>
      <aside className="nqd-drawer" role="dialog" aria-label="New quote" onClick={(e) => e.stopPropagation()}>
        {draftId ? (
          // The real builder, embedded. Close returns to the card's Quotes tab.
          <QuoteDetail quoteId={draftId} onClose={onClose} embedded />
        ) : (
          <div className="nqd-start">
            <div className="nqd-start-head">
              <h2>New quote</h2>
              <button type="button" className="nqd-close" aria-label="Close" onClick={onClose}>×</button>
            </div>
            <p className="nqd-sub">for {customerName || "this customer"}</p>

            {error ? <p className="nqd-error">{error}</p> : null}

            <button
              type="button"
              className="nqd-bespoke"
              disabled={creating}
              onClick={() => createDraft(null)}
            >
              <span className="nqd-bespoke-title">Bespoke quote</span>
              <span className="nqd-bespoke-sub">Start empty — build your own options and pricing</span>
            </button>

            <div className="nqd-or">or start from a service</div>

            {offerings === null ? (
              <p className="nqd-muted">Loading services…</p>
            ) : services.length === 0 ? (
              <p className="nqd-muted">No services are set up for this office yet — use a bespoke quote.</p>
            ) : (
              <ul className="nqd-services">
                {services.map((o) => (
                  <li key={o.id}>
                    <button
                      type="button"
                      className="nqd-service"
                      disabled={creating}
                      onClick={() => createDraft(o.id)}
                    >
                      <span className="nqd-service-name">{o.name}</span>
                      <span className="nqd-service-price">{money(o.base_price)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {creating ? <p className="nqd-muted">Starting the quote…</p> : null}

            <div className="nqd-start-actions">
              <button type="button" className="nqd-cancel" onClick={onClose} disabled={creating}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
